import { FC, useCallback, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
  ProductFormData,
  RithmicInstrument,
} from '@src/types/productsAddons.ts';
import { Button, Card, RadixTable, TColumn } from '@features/ui';
import {
  ProductAdditionalForm,
  ProductAvailabilityForm,
  ProductBillingForm,
  ProductGeneralSettingsForm,
  ProductInstrumentsForm,
  ProductOptionsForm,
  ProductTypeForm,
  ProductQualificationForm,
  ProductPayoutLogicForm,
} from '@features/admin/productsAddons';
import { TOption } from '@src/types';
import {
  getDisableNonPerformanceAccount,
  getIsNoneTrading,
} from '@lib/products.ts';
import { capitalize, formatDate } from '@lib/utils.ts';

export const ProductForm: FC<
  UseFormReturn<ProductFormData> & {
    productOptions: TOption[];
    instrumentData?: RithmicInstrument[];
    isEdit?: boolean;
  }
> = ({ productOptions, isEdit, instrumentData, ...methods }) => {
  const categories = methods.watch('category');
  const isPerformanceAccount = methods.watch('is_performance_account');
  const accountType = methods.watch('account_type');

  const isNonTrading = getIsNoneTrading(categories);
  const disabled = getDisableNonPerformanceAccount(
    categories,
    isPerformanceAccount,
    accountType
  );

  const columns = useMemo(
    (): TColumn<RithmicInstrument>[] => [
      {
        title: <p className={'ml-1'}>Date</p>,
        className: 'h-[49px]',
        render: (item) => (
          <div className={`h-full flex items-center px-1`}>
            <p>{formatDate(item?.created_at)}</p>
          </div>
        ),
      },
      {
        title: <p className={'ml-1'}>Type</p>,
        className: 'h-[49px]',
        render: (item) => (
          <div className={`h-full flex items-center px-1`}>
            <p>{capitalize(item?.type || '')}</p>
          </div>
        ),
      },
    ],
    []
  );

  const handleDownloadFile = useCallback((item: RithmicInstrument) => {
    const apiUrl = import.meta.env.VITE_API_BASE_URL;
    const baseUrl = apiUrl.replace(/\/api\/v1$/, '');

    window.open(`${baseUrl}/assets/${item.rithmic_csv_package}`, '_blank');
  }, []);

  return (
    <Card className={'pl-[32px] lg:pr-[54px] pr-[32px]'}>
      <ProductGeneralSettingsForm {...methods} />
      <ProductBillingForm {...methods} />
      <ProductAvailabilityForm productOptions={productOptions} {...methods} />
      <ProductOptionsForm {...methods} />
      <ProductTypeForm
        isEdit={isEdit}
        productOptions={productOptions}
        {...methods}
      />
      <fieldset
        disabled={isNonTrading}
        style={{ opacity: isNonTrading ? 0.5 : 1 }}
      >
        <ProductInstrumentsForm disabled={isNonTrading} {...methods} />
      </fieldset>
      {instrumentData?.length ? (
        <div className={'lg:w-[50%] mb-[33px] lg:ml-10 ml-5'}>
          <RadixTable
            columns={columns}
            data={instrumentData}
            onSelect={handleDownloadFile}
          />
        </div>
      ) : null}
      <fieldset disabled={disabled} style={{ opacity: disabled ? 0.5 : 1 }}>
        <ProductQualificationForm {...methods} />
        <ProductPayoutLogicForm {...methods} />
        <ProductAdditionalForm disabled={disabled} {...methods} />
      </fieldset>
      <div className={'flex justify-end'}>
        <Button
          className={'mt-[82px] mb-[66px]'}
          loading={methods.formState.isSubmitting}
          size={'sm'}
          variant={'lime'}
        >
          Save
        </Button>
      </div>
    </Card>
  );
};
